/**
 * GEDCOM Preview Validation Utility
 *
 * Validates parsed GEDCOM data before import and generates validation report.
 * Part of Phase 4 - Slice 5: Validation Framework
 */

import { TFunction } from 'i18next';
import { ParsedPerson } from '../parsers/indiParser';
import { ResolvedFamily } from '../types/GedcomFamily';
import { ValidationResult, ValidationIssue, ValidationIssueType, ReadinessStatus } from '../types/GedcomValidation';

/**
 * Message builder that supports i18n
 */
function buildValidationMessage(
  t: TFunction,
  type: ValidationIssueType,
  params?: Record<string, string | string[]>
): string {
  switch (type) {
    case 'no_individuals':
      return t('gedcom.validation.errors.noIndividuals', 'No individuals found in GEDCOM file. Import cannot proceed.');

    case 'duplicate_pointer':
      return t('gedcom.validation.errors.duplicatePointer', 'Duplicate individual ID: {{id}}', { id: params?.id || '' });

    case 'unresolved_reference':
      return t('gedcom.validation.warnings.unresolvedReference',
        'Family {{familyId}} has unresolved references: {{refs}}',
        { familyId: params?.familyId || '', refs: params?.refs || '' }
      );

    case 'missing_spouse_tag':
      return t('gedcom.validation.warnings.missingSpouseTag',
        'Family {{familyId}} has no HUSB or WIFE tags',
        { familyId: params?.familyId || '' }
      );

    case 'missing_name':
      return t('gedcom.validation.warnings.missingName',
        'Individual {{id}} has no name',
        { id: params?.id || '' }
      );

    case 'unknown_gender':
      return t('gedcom.validation.warnings.unknownGender',
        'Individual {{id}} ({{name}}) has unknown gender',
        { id: params?.id || '', name: params?.name || '' }
      );

    case 'birth_after_death':
      return t('gedcom.validation.errors.birthAfterDeath',
        'Individual {{id}} ({{name}}) has birth year {{birthYear}} after death year {{deathYear}}',
        {
          id: params?.id || '',
          name: params?.name || '',
          birthYear: params?.birthYear || '',
          deathYear: params?.deathYear || ''
        }
      );

    case 'marriage_after_divorce':
      return t('gedcom.validation.errors.marriageAfterDivorce',
        'Family {{familyId}} has marriage year {{marriageYear}} after divorce year {{divorceYear}}',
        {
          familyId: params?.familyId || '',
          marriageYear: params?.marriageYear || '',
          divorceYear: params?.divorceYear || ''
        }
      );

    case 'impossible_parent_age':
      return t('gedcom.validation.warnings.impossibleParentAge',
        '{{parentName}} was {{age}} years old when child {{childName}} was born',
        {
          parentName: params?.parentName || '',
          age: params?.age || '',
          childName: params?.childName || ''
        }
      );

    case 'one_parent_family':
      return t('gedcom.validation.info.oneParentFamily',
        'Family {{familyId}} has only one parent',
        { familyId: params?.familyId || '' }
      );

    case 'spouse_only_family':
    case 'childless_family':
      return t('gedcom.validation.info.childlessFamily',
        'Family {{familyId}} has no children',
        { familyId: params?.familyId || '' }
      );

    case 'self_referencing_parent':
      return t('gedcom.validation.errors.selfReferencingParent',
        'Individual {{id}} ({{name}}) is their own parent in family {{familyId}}',
        {
          id: params?.id || '',
          name: params?.name || '',
          familyId: params?.familyId || ''
        }
      );

    default:
      return t('gedcom.validation.errors.unknown', 'Unknown validation error');
  }
}

export function validateGedcomPreview(
  persons: ParsedPerson[],
  families: ResolvedFamily[],
  t: TFunction
): ValidationResult {
  const issues: ValidationIssue[] = [];

  // 1. BLOCKING: No individuals found
  if (persons.length === 0) {
    issues.push({
      severity: 'error',
      type: 'no_individuals',
      message: buildValidationMessage(t, 'no_individuals'),
    });
  }

  // 2. Check for duplicate GEDCOM IDs
  const seenIds = new Set<string>();
  for (const person of persons) {
    if (seenIds.has(person.gedcomId)) {
      issues.push({
        severity: 'error',
        type: 'duplicate_pointer',
        message: buildValidationMessage(t, 'duplicate_pointer', { id: person.gedcomId }),
        gedcomId: person.gedcomId,
      });
    }
    seenIds.add(person.gedcomId);
  }

  // 3. Check for unresolved references in families
  for (const family of families) {
    if (family.unresolvedRefs && family.unresolvedRefs.length > 0) {
      issues.push({
        severity: 'warning',
        type: 'unresolved_reference',
        message: buildValidationMessage(t, 'unresolved_reference', {
          familyId: family.familyId,
          refs: family.unresolvedRefs.join(', ')
        }),
        gedcomId: family.familyId,
        relatedIds: family.unresolvedRefs,
      });
    }
  }

  // 4. Check for missing spouse tags (different from unresolved)
  for (const family of families) {
    const missingSpouse = !family.husband && !family.wife;
    const onlyOneSpouse = (!family.husband && family.wife) || (family.husband && !family.wife);

    if (missingSpouse) {
      issues.push({
        severity: 'warning',
        type: 'missing_spouse_tag',
        message: buildValidationMessage(t, 'missing_spouse_tag', { familyId: family.familyId }),
        gedcomId: family.familyId,
      });
    } else if (onlyOneSpouse) {
      issues.push({
        severity: 'info',
        type: 'one_parent_family',
        message: buildValidationMessage(t, 'one_parent_family', { familyId: family.familyId }),
        gedcomId: family.familyId,
      });
    }
  }

  // 5. Check for families with no children (spouse-only)
  for (const family of families) {
    if (family.children.length === 0) {
      issues.push({
        severity: 'info',
        type: 'childless_family',
        message: buildValidationMessage(t, 'childless_family', { familyId: family.familyId }),
        gedcomId: family.familyId,
      });
    }
  }

  // 6. Check for missing names
  for (const person of persons) {
    if (!person.givenName && !person.surname) {
      issues.push({
        severity: 'warning',
        type: 'missing_name',
        message: buildValidationMessage(t, 'missing_name', { id: person.gedcomId }),
        gedcomId: person.gedcomId,
      });
    }
  }

  // 7. Check for unknown gender
  for (const person of persons) {
    if (person.gender === 'other') {
      issues.push({
        severity: 'warning',
        type: 'unknown_gender',
        message: buildValidationMessage(t, 'unknown_gender', {
          id: person.gedcomId,
          name: person.displayName
        }),
        gedcomId: person.gedcomId,
      });
    }
  }

  // 8. Check birth after death
  for (const person of persons) {
    if (person.birthYear && person.deathYear && person.birthYear > person.deathYear) {
      issues.push({
        severity: 'error',
        type: 'birth_after_death',
        message: buildValidationMessage(t, 'birth_after_death', {
          id: person.gedcomId,
          name: person.displayName,
          birthYear: String(person.birthYear),
          deathYear: String(person.deathYear)
        }),
        gedcomId: person.gedcomId,
      });
    }
  }

  // 9. Check marriage after divorce
  for (const family of families) {
    if (
      family.marriageYear &&
      family.divorceYear &&
      family.marriageYear > family.divorceYear
    ) {
      issues.push({
        severity: 'error',
        type: 'marriage_after_divorce',
        message: buildValidationMessage(t, 'marriage_after_divorce', {
          familyId: family.familyId,
          marriageYear: String(family.marriageYear),
          divorceYear: String(family.divorceYear)
        }),
        gedcomId: family.familyId,
      });
    }
  }

  // 10. Check impossible parent ages (child born before parent was 13)
  for (const family of families) {
    const husband = family.husband;
    const wife = family.wife;

    for (const child of family.children) {
      if (husband?.birthYear && child.birthYear) {
        const parentAge = child.birthYear - husband.birthYear;
        if (parentAge < 13) {
          issues.push({
            severity: 'warning',
            type: 'impossible_parent_age',
            message: buildValidationMessage(t, 'impossible_parent_age', {
              parentName: husband.displayName,
              age: String(parentAge),
              childName: child.displayName
            }),
            gedcomId: family.familyId,
            relatedIds: [husband.gedcomId, child.gedcomId],
          });
        }
      }

      if (wife?.birthYear && child.birthYear) {
        const parentAge = child.birthYear - wife.birthYear;
        if (parentAge < 13) {
          issues.push({
            severity: 'warning',
            type: 'impossible_parent_age',
            message: buildValidationMessage(t, 'impossible_parent_age', {
              parentName: wife.displayName,
              age: String(parentAge),
              childName: child.displayName
            }),
            gedcomId: family.familyId,
            relatedIds: [wife.gedcomId, child.gedcomId],
          });
        }
      }
    }
  }

  // 11. Check for self-referencing parents (individual is their own parent)
  for (const family of families) {
    const husband = family.husband;
    const wife = family.wife;

    for (const child of family.children) {
      // Check if child is same as father
      if (husband && child.gedcomId === husband.gedcomId) {
        issues.push({
          severity: 'error',
          type: 'self_referencing_parent',
          message: buildValidationMessage(t, 'self_referencing_parent', {
            id: child.gedcomId,
            name: child.displayName,
            familyId: family.familyId
          }),
          gedcomId: child.gedcomId,
          relatedIds: [family.familyId],
        });
      }

      // Check if child is same as mother
      if (wife && child.gedcomId === wife.gedcomId) {
        issues.push({
          severity: 'error',
          type: 'self_referencing_parent',
          message: buildValidationMessage(t, 'self_referencing_parent', {
            id: child.gedcomId,
            name: child.displayName,
            familyId: family.familyId
          }),
          gedcomId: child.gedcomId,
          relatedIds: [family.familyId],
        });
      }
    }
  }

  // Calculate summary
  const errorCount = issues.filter(i => i.severity === 'error').length;
  const warningCount = issues.filter(i => i.severity === 'warning').length;
  const infoCount = issues.filter(i => i.severity === 'info').length;

  // Determine readiness status
  let status: ReadinessStatus = 'ready';
  if (errorCount > 0) {
    status = 'blocked';
  } else if (warningCount > 0 || infoCount > 0) {
    status = 'ready_with_warnings';
  }

  return {
    status,
    summary: {
      totalIndividuals: persons.length,
      totalFamilies: families.length,
      errorCount,
      warningCount,
      infoCount,
    },
    issues,
  };
}
